import { FeatureState, isFeatureEnabled } from '@etrigan/feature-toggles-client'
import {
    getFundingType,
    isBauhausSidebar,
    useProduct,
} from '@news-mono/component-library'
import {
    CardItem,
    CollectionEvent,
    createCardClickedEvent,
    createCollectionAvailableEvent,
    isPublicationCardItem,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { BauhausCard } from '../../cards/BauhausCard/BauhausCard'
import { StyledBauhausCardListItemWrapper } from '../../cards/BauhausCard/BauhausCard.styled'
import { ListCard } from '../../cards/ListCard/ListCard'
import {
    StyledGrid,
    StyledGridColumn,
    StyledGridHeaderRow,
    StyledGridRow,
    StyledLandscapeItem,
    StyledList,
    StyledSectionHeader,
    StyledSimpleHeader,
} from '../../collections/November/November.styled'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import { SectionHeaderProps } from '../../section-header/SectionHeader/SectionHeader'
import { ThemeOverrider } from '../../themes/ThemeOverrider/ThemeOverrider'
import { CardSwitcher } from '../../__helpers/CardSwitcher'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'
import { OverrideThemeSection } from '../../__helpers/override-theme-section'
import { getBauhausThemeOverride } from '../../__styling/get-bauhaus-theme-override'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { Section } from '../../__styling/settings/sections'
import { ThemedRender } from '../../__styling/themed-render'
import { invertMaybeLoadedItems } from '../helpers/loading'
import { HeaderV2 } from '../../SevenNewsV2/HeaderV2/HeaderV2'
import { InlineContentCard } from '../../SevenNewsV2/cards/InlineContentCard'
import { StyledLinkWrapper } from '../../SevenNewsV2/content/InlineContent/InlineContentCards.styled'

export interface NovemberProps {
    className?: string
    timestamp?: TimestampType
    hasBackground?: boolean
    verticalSpacing?: keyof ThemeMargins | undefined
    sectionHeader?: SectionHeaderProps
    section?: Section
    disableImpressionEvent?: boolean
    cardNumberOffset?: number
    noStretch?: boolean
    truncateListCards?: boolean
    onEvent: (event: CollectionEvent) => void
    expectedCards: number
    items: MaybeLoaded<CardItem[]>
    contentIsTangential?: boolean
    currentPath?: string
    toggles?: FeatureState
}

export const November: React.FC<NovemberProps> = (props) => {
    const product = useProduct()

    const isV2ArticlePageEnabled =
        props.toggles &&
        isFeatureEnabled(props.toggles, '7-news-2-article-page')

    const storyClassification = useFeature('story-classification')

    function renderListCards() {
        const { timestamp, truncateListCards, onEvent } = props
        const items = invertMaybeLoadedItems(props.items, props.expectedCards)
        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            onEvent={onEvent}
                            key={cardNum}
                            item={item}
                            cardContext="november-list"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => (
                                <ListCard
                                    key={index}
                                    item={publicationItem}
                                    timestamp={timestamp}
                                    onEvent={onEvent}
                                    cardNumber={cardNum}
                                    isTruncated={truncateListCards}
                                />
                            )}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    function renderBauhausListCards() {
        const { timestamp, truncateListCards, onEvent, currentPath, toggles } =
            props
        const items = invertMaybeLoadedItems(props.items, props.expectedCards)

        const isBauhaus = toggles
            ? isBauhausSidebar(toggles, currentPath)
            : false

        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            onEvent={onEvent}
                            key={cardNum}
                            item={item}
                            cardContext="november-list"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => {
                                if (
                                    publicationItem.loaded &&
                                    isPublicationCardItem(
                                        publicationItem.result,
                                    )
                                ) {
                                    const themeOverride =
                                        getBauhausThemeOverride(
                                            String(currentPath),
                                        )
                                    if (isBauhaus) {
                                        return (
                                            <StyledBauhausCardListItemWrapper>
                                                <ThemeOverrider
                                                    override={themeOverride}
                                                >
                                                    {isV2ArticlePageEnabled ? (
                                                        <StyledLinkWrapper
                                                            to={toLinkWithHint(
                                                                item,
                                                            )}
                                                            onClick={(
                                                                event,
                                                            ) => {
                                                                props.onEvent(
                                                                    createCardClickedEvent(
                                                                        props.onEvent,
                                                                        publicationItem.result,
                                                                        'SevenNewsInlineRelatedContentCard',
                                                                        'InlineRelatedContent',
                                                                        cardNum,
                                                                        getFundingType(
                                                                            publicationItem.result,
                                                                            storyClassification,
                                                                        ),
                                                                        'InlineRelatedCollection',
                                                                    ),
                                                                )
                                                            }}
                                                        >
                                                            <InlineContentCard
                                                                item={
                                                                    publicationItem
                                                                }
                                                                isSideBarCmp={
                                                                    true
                                                                }
                                                            />
                                                        </StyledLinkWrapper>
                                                    ) : (
                                                        <BauhausCard
                                                            item={
                                                                publicationItem
                                                            }
                                                            cardNumber={cardNum}
                                                            onEvent={onEvent}
                                                            teaserMode="hidden"
                                                            cardType="small"
                                                            verticalSpacing="md"
                                                            hasPadding={true}
                                                        />
                                                    )}
                                                </ThemeOverrider>
                                            </StyledBauhausCardListItemWrapper>
                                        )
                                    } else {
                                        return (
                                            <ListCard
                                                key={index}
                                                item={publicationItem}
                                                timestamp={timestamp}
                                                onEvent={onEvent}
                                                cardNumber={cardNum}
                                                isTruncated={truncateListCards}
                                            />
                                        )
                                    }
                                } else {
                                    return null
                                }
                            }}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    function renderLandscapeCards() {
        const { timestamp, onEvent } = props
        const items = invertMaybeLoadedItems(props.items, props.expectedCards)

        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            onEvent={onEvent}
                            key={cardNum}
                            item={item}
                            cardContext="november-landscape"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => (
                                <StyledLandscapeItem
                                    key={cardNum}
                                    item={publicationItem}
                                    mediaMode="hidden"
                                    asListElement
                                    cardNumber={
                                        cardNum + (props.cardNumberOffset || 0)
                                    }
                                    onEvent={onEvent}
                                    timestamp={timestamp}
                                    hideByline
                                    teaserMode="hidden"
                                />
                            )}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    function renderSectionHeader() {
        const { sectionHeader, currentPath, toggles } = props

        if (isV2ArticlePageEnabled) {
            return <HeaderV2 sectionHeader={{ heading: 'Top Headlines' }} />
        }

        const isBauhaus = toggles
            ? isBauhausSidebar(toggles, currentPath)
            : false
        // Only render section header when there are items and it's specified
        return (
            sectionHeader &&
            (!props.items.loaded || props.items.result.length) && (
                <StyledGridHeaderRow canGrow={sectionHeader.canGrow}>
                    <StyledGridColumn>
                        {isBauhaus ? (
                            <StyledSimpleHeader>
                                {sectionHeader.heading}
                            </StyledSimpleHeader>
                        ) : (
                            <StyledSectionHeader
                                {...sectionHeader}
                                canGrow={sectionHeader.canGrow || true}
                                showClock={true}
                            />
                        )}
                    </StyledGridColumn>
                </StyledGridHeaderRow>
            )
        )
    }

    function renderNovember() {
        const {
            className,
            verticalSpacing,
            contentIsTangential,
            currentPath,
            toggles,
        } = props
        const isBauhaus = toggles
            ? isBauhausSidebar(toggles, currentPath)
            : false

        return (
            <ImpressionAvailable
                loading={!props.items.loaded}
                available={() => {
                    if (props.disableImpressionEvent) {
                        return
                    }
                    if (!props.items.loaded) {
                        console.warn(
                            'Available should never be called when loading is true',
                        )
                        return
                    }
                    props.onEvent(
                        createCollectionAvailableEvent(
                            props.items.result,
                            'November',
                            product,
                            props.onEvent,
                        ),
                    )
                }}
            >
                {(ref) => (
                    <StyledGrid
                        ref={ref}
                        className={className}
                        verticalSpacing={verticalSpacing}
                        noStretch={props.noStretch}
                        as={contentIsTangential ? 'aside' : 'div'}
                    >
                        {renderSectionHeader()}
                        <StyledGridRow hasBackground={props.hasBackground}>
                            <StyledGridColumn>
                                <StyledList
                                    removeBorderLast={
                                        isBauhaus ? true : props.hasBackground
                                    }
                                >
                                    <ThemedRender
                                        perthnow={() => renderListCards()}
                                        sevennews={() =>
                                            isBauhaus
                                                ? renderBauhausListCards()
                                                : renderListCards()
                                        }
                                        thewest={() => renderLandscapeCards()}
                                    />
                                </StyledList>
                            </StyledGridColumn>
                        </StyledGridRow>
                    </StyledGrid>
                )}
            </ImpressionAvailable>
        )
    }

    return (
        <OverrideThemeSection
            section={props.section ? props.section : 'default'}
        >
            {renderNovember()}
        </OverrideThemeSection>
    )
}
November.displayName = 'November'
